/**
 * useLearnerContext — Gedeeld leerling-aggregaat voor het mascot-domein.
 *
 * DDD: voorheen werd deze berekening op TWEE plekken gedupliceerd:
 *   - FloatingDjay.tsx (voor proactieve messages)
 *   - useVariantUnlocks.ts (voor variant unlocks)
 * Beide itereerden MODULES, telden answered-questions en mastery-counts.
 *
 * Eén bron van waarheid voorkomt dat de mastery-drempel op twee plekken
 * gewijzigd moet worden, en levert dezelfde memoization-key voor beide
 * consumers.
 *
 * Ronde 7: `dayOfWeek` ververst nu automatisch om middernacht. Voorheen
 * bleef de waarde bevroren zolang de component gemount was — als een
 * leerling de app over middernacht heen open hield, kreeg Djay nog steeds
 * berichten gebaseerd op de vorige dag. Zeldzaam maar nu gehard.
 *
 * Audit-referentie: docs/audits/MASCOT_DOMAIN_AUDIT_2026.md §2.2 DDD-3, §5.
 */

import { useEffect, useMemo, useState } from 'react';
import { useAppStore } from '../app/hooks/useAppStore';
import { MODULES } from '../content';
import { DJAY_THRESHOLDS } from './config';
import type { LearnerContext } from './djayMessages';

/**
 * Returns the current day-of-week (0-6) and self-refreshes at midnight.
 * Internal to this module; kept here to avoid a public hook for a single
 * consumer.
 */
function useDayOfWeek(): number {
  const [day, setDay] = useState(() => new Date().getDay());
  useEffect(() => {
    const now = new Date();
    const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 1) // +1s buffer so getDay() is reliably the new day
;
    const msUntilMidnight = nextMidnight.getTime() - now.getTime();
    const timer = setTimeout(() => {
      setDay(new Date().getDay());
    }, msUntilMidnight);
    return () => clearTimeout(timer);
  }, [day]);
  return day;
}

/**
 * Volledige leerling-context inclusief afgeleide aggregaten.
 * Wordt gememoized op alle relevante store-velden + dayOfWeek tick.
 */
export function useLearnerContext(): LearnerContext {
  const {
    state,
    activeAccount,
    getModuleMasteryForGroup,
    getAnsweredQuestions
  } = useAppStore();
  const displayName = activeAccount?.displayName || 'vriend';
  const streak = state?.streak?.currentTotalStreak || 0;
  const level = state?.stats?.level || 1;
  const xp = state?.stats?.xp || 0;
  const dayOfWeek = useDayOfWeek();
  return useMemo<LearnerContext>(() => {
    let masteredCount = 0;
    let totalQuestionsAnswered = 0;
    MODULES.forEach(m => {
      totalQuestionsAnswered += getAnsweredQuestions(m.id).length;
      if (getModuleMasteryForGroup(m.id) >= DJAY_THRESHOLDS.MODULE_MASTERY_PCT) {
        masteredCount += 1;
      }
    });
    return {
      displayName,
      streak,
      level,
      xp,
      masteredCount,
      totalQuestionsAnswered,
      dayOfWeek
    };
  }, [displayName, streak, level, xp, dayOfWeek, getModuleMasteryForGroup, getAnsweredQuestions]);
}